import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ClipboardList, X } from "lucide-react";

const TYPE_LABEL = {
  radio_yes_no: "Yes / No",
  dropdown: "Dropdown",
  checkbox: "Checkbox",
  text: "Short text",
  number: "Number",
};

const empty = () => ({
  id: null,
  label: "",
  type: "radio_yes_no",
  options: [],
  required: false,
});

/**
 * Admin-only management UI for custom Job Entry fields. Employees see the
 * resulting fields rendered on their job entry form.
 */
export default function CustomJobFieldsManager() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/custom-job-fields");
      setFields(data.fields || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty()); setDialogOpen(true); };
  const openEdit = (f) => { setForm({ ...f, options: [...(f.options || [])] }); setDialogOpen(true); };

  const save = async () => {
    if (!form.label.trim()) {
      toast.error("Label is required");
      return;
    }
    if (form.type === "dropdown" && (form.options.filter((o) => o.trim()).length < 2)) {
      toast.error("Dropdown needs at least 2 options");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        label: form.label.trim(),
        type: form.type,
        options: (form.options || []).map((o) => o.trim()).filter(Boolean),
        required: !!form.required,
      };
      if (form.id) {
        await api.put(`/settings/custom-job-fields/${form.id}`, payload);
        toast.success("Field updated");
      } else {
        await api.post("/settings/custom-job-fields", payload);
        toast.success("Field added");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!confirm(`Delete "${f.label}"? This won't affect job entries already saved with this field — they'll just stop showing it.`)) return;
    try {
      await api.delete(`/settings/custom-job-fields/${f.id}`);
      toast.success("Field deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const reorder = async (idx, dir) => {
    const next = [...fields];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setFields(next); // optimistic
    try {
      await api.post("/settings/custom-job-fields/reorder", { ordered_ids: next.map((f) => f.id) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      await load();
    }
  };

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="custom-job-fields-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            <ClipboardList className="h-5 w-5 text-sky-700 mt-0.5" />
            <div>
              <h3 className="font-bold font-display text-base">Job Entry Custom Fields</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Add task-verification fields (Yes/No, Dropdown, Checkbox, Text, or Number) to the employee job-entry form. They'll appear on the daily PDF and in the submission detail.
              </p>
            </div>
          </div>
          <Button onClick={openAdd} className="bg-sky-600 hover:bg-sky-700" data-testid="add-custom-field-btn">
            <Plus className="h-4 w-4 mr-1" /> Add field
          </Button>
        </div>

        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && fields.length === 0 && (
          <p className="text-sm text-slate-500 italic px-3 py-4 border border-dashed border-slate-200 rounded">
            No custom fields yet. Click "Add field" to start.
          </p>
        )}

        <div className="space-y-2">
          {fields.map((f, idx) => (
            <div key={f.id} className="border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid={`custom-field-row-${f.id}`}>
              <div className="flex flex-col gap-0">
                <Button variant="ghost" size="sm" onClick={() => reorder(idx, -1)} disabled={idx === 0} className="h-5 w-5 p-0" data-testid={`field-up-${f.id}`}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => reorder(idx, 1)} disabled={idx === fields.length - 1} className="h-5 w-5 p-0" data-testid={`field-down-${f.id}`}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-900">{f.label}</p>
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                    {TYPE_LABEL[f.type] || f.type}
                  </span>
                  {f.required && (
                    <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700">
                      Required
                    </span>
                  )}
                </div>
                {f.type === "dropdown" && (f.options || []).length > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5 truncate">Options: {f.options.join(", ")}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openEdit(f)} className="h-8 px-2 text-slate-600 hover:text-sky-700" data-testid={`edit-field-${f.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(f)} className="h-8 px-2 text-slate-600 hover:text-red-700" data-testid={`delete-field-${f.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="custom-field-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? "Edit Field" : "Add Custom Field"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Label *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g., Yard Sign Placed"
                className="mt-1"
                data-testid="field-label-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Field Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1" data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="radio_yes_no">Yes / No (radio)</SelectItem>
                  <SelectItem value="dropdown">Dropdown</SelectItem>
                  <SelectItem value="checkbox">Checkbox</SelectItem>
                  <SelectItem value="text">Short text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === "dropdown" && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Options (one per row)</Label>
                <div className="mt-1 space-y-2">
                  {(form.options.length === 0 ? [""] : form.options).map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={opt}
                        onChange={(e) => {
                          const next = [...form.options];
                          while (next.length <= idx) next.push("");
                          next[idx] = e.target.value;
                          setForm({ ...form, options: next });
                        }}
                        placeholder={`Option ${idx + 1}`}
                        data-testid={`field-option-input-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setForm({ ...form, options: form.options.filter((_, i) => i !== idx) })}
                        className="h-8 px-2 text-slate-500"
                        data-testid={`field-option-remove-${idx}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, options: [...form.options, ""] })} data-testid="field-add-option-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add option
                  </Button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                checked={!!form.required}
                onCheckedChange={(v) => setForm({ ...form, required: v })}
                data-testid="field-required-switch"
              />
              <span className="text-sm text-slate-700">Required — employee must fill before saving job</span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="save-field-btn">
              {busy ? "Saving…" : (form.id ? "Save changes" : "Add field")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
